import { FaUserFriends } from "react-icons/fa";
import { MdStyle } from "react-icons/md";
import {
	MdAdminPanelSettings,
	MdDashboardCustomize,
	MdOutlinePublic,
} from "react-icons/md";
import { IoIosCheckmarkCircle, IoIosCloseCircle } from "react-icons/io";
import { Typography, message, Spin } from "antd";

const { Text } = Typography;
const GLOBAL_PERMISSION = "Global";
const PRODUCT_PERMISSION = "Product";
const CATALOGUE_PERMISSION = "Catalogue";

const subPermissionType = {
	RADIO: "RADIO",
	DROPDOWN_CHECKBOX: "DROPDOWN_CHECKBOX",
	NA: "NA",
	LIST: "LIST",
};

const Colors = {
	adminBgColor: "#E6DFFB",
	adminColor: "#835FEA",
	employeeBgColor: "#BBD3F7",
	employeeColor: "#4989E9",
	adminTabColor: "#F3EFFD",
};

const tranformPermissionsForModal = (permissions = []) => {
	const globalPermissions = [];
	const productPermissions = [];
	const cataloguePermissions = [];
	permissions.forEach((p) => {
		if (p.permissionType === GLOBAL_PERMISSION) {
			globalPermissions.push(p);
		} else if (p.permissionType === PRODUCT_PERMISSION) {
			productPermissions.push(p);
		} else if (p.permissionType === CATALOGUE_PERMISSION) {
			cataloguePermissions.push(p);
		}
	});
	return [
		{
			type: PRODUCT_PERMISSION,
			permissions: productPermissions.sort((a, b) => a.order - b.order),
		},
		{
			type: CATALOGUE_PERMISSION,
			permissions: cataloguePermissions.sort((a, b) => a.order - b.order),
		},
		{
			type: GLOBAL_PERMISSION,
			permissions: globalPermissions.sort((a, b) => a.order - b.order),
		},
	];
};

const tranformPermissions = (permissions = []) => {
	const globalPermissions = [];
	const productPermissions = [];
	const cataloguePermissions = [];
	permissions.forEach((p) => {
		if (p.permissionType === GLOBAL_PERMISSION) {
			globalPermissions.push(p);
		} else if (p.permissionType === PRODUCT_PERMISSION) {
			productPermissions.push(p);
		} else if (p.permissionType === CATALOGUE_PERMISSION) {
			cataloguePermissions.push(p);
		}
	});
	return [
		{ type: GLOBAL_PERMISSION, permissions: globalPermissions },
		{ type: PRODUCT_PERMISSION, permissions: productPermissions },
		{
			type: CATALOGUE_PERMISSION,
			permissions: cataloguePermissions,
		},
	];
};

const AccessTypeIcon = ({ isAdmin, size }) => {
	if (isAdmin)
		return <MdAdminPanelSettings size={size} color={Colors.adminColor} />;
	return <FaUserFriends size={size} color={Colors.employeeColor} />;
};

const AccessTypeBox = ({ isAdmin, iconSize, styles = {} }) => {
	return (
		<div
			style={{
				flexDirection: "row",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				borderRadius: "4px",
				backgroundColor: isAdmin ? Colors.adminBgColor : Colors.employeeBgColor,
				...styles,
			}}
		>
			<AccessTypeIcon isAdmin={isAdmin} size={iconSize} />
		</div>
	);
};

const PermissionTypeIcon = ({ name, color, size }) => {
	if (name === PRODUCT_PERMISSION) return <MdStyle size={size} color={color} />;
	if (name === CATALOGUE_PERMISSION)
		return <MdDashboardCustomize size={size} color={color} />;
	if (name === GLOBAL_PERMISSION)
		return <MdOutlinePublic size={size} color={color} />;
	return <MdOutlinePublic size={size} color={color} />;
};

const PermissionTypeBox = ({
	iconColor,
	iconBgColor,
	name,
	iconSize,
	styles = {},
}) => {
	return (
		<div
			style={{
				flexDirection: "row",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				borderRadius: "4px",
				backgroundColor: iconBgColor,
				...styles,
			}}
		>
			<PermissionTypeIcon color={iconColor} name={name} size={iconSize} />
		</div>
	);
};

const LineBreak = ({ size = "2rem" }) => (
	<div
		style={{
			marginBottom: size,
			marginTop: size,
			height: "1px",
			backgroundColor: "#E6E7E7",
		}}
	/>
);

const transformToAllPermissions = (permissions = []) => {
	const a = permissions.map((p) => {
		return {
			permissionId: p.permissionId,
			slug: p.slug,
			toggleValue: p.toggleValue,
			permissionType: p.permissionType,
			subPermission: {
				values: p.subPermission.values || [],
			},
		};
	});
	return Object.fromEntries(a.map((item) => [item.permissionId, item]));
};

const LabelIcon = ({ toggleValue, isModuleOff = false }) => {
	if (isModuleOff) return <IoIosCloseCircle color="#F34D4D" size="1rem" />;
	if (toggleValue) return <IoIosCheckmarkCircle color="#7DB8AD" size="1rem" />;
	return <IoIosCloseCircle color="#B4B7B6" size="1rem" />;
};

const LabelRow = ({ label, toggleValue, isModuleOff = false }) => {
	return (
		<div style={{ flexDirection: "row", display: "flex" }}>
			<div
				style={{
					flexDirection: "column",
					display: "flex",
					justifyContent: "center",
				}}
			>
				<LabelIcon toggleValue={toggleValue} isModuleOff={isModuleOff} />
			</div>
			<Text
				style={{
					marginLeft: "0.5rem",
					color: "#686E6D",
					fontSize: "14px",
				}}
			>
				{label}
			</Text>
		</div>
	);
};

const calCountPerCategory = (allPermissions = {}) => {
	const result = {
		[PRODUCT_PERMISSION]: {
			totalCount: 0,
			currentCount: 0,
		},
		[CATALOGUE_PERMISSION]: {
			totalCount: 0,
			currentCount: 0,
		},
		[GLOBAL_PERMISSION]: {
			totalCount: 0,
			currentCount: 0,
		},
	};
	Object.values(allPermissions).forEach((permission) => {
		const type = permission.permissionType;
		if (
			permission.slug === "view_products" &&
			permission.subPermission.values.includes("current_user")
		) {
			result[type].totalCount = result[type].totalCount + 1;
			if (permission.toggleValue)
				result[type].currentCount = result[type].currentCount + 0.5;
		} else if (!permission.slug.includes("grantAll_")) {
			result[type].totalCount = result[type].totalCount + 1;
			if (permission.toggleValue)
				result[type].currentCount = result[type].currentCount + 1;
		}
	});
	return result;
};

const evalIfToggleIsDisabled = (parentDeps = [], allPermissions = {}) => {
	return parentDeps.some((permissionId) => {
		return !allPermissions?.[permissionId]?.toggleValue;
	});
};

const isDependantOnGrantAll = (data = [], allPermissions) => {
	return data?.parentDeps?.some((pId) => {
		const slug = allPermissions[pId].slug;
		return slug.includes("grantAll_");
	});
};

const getNewPermissionsData = (allPermissions, data, value) => {
	const { permissionId } = data;
	if (!value && isDependantOnGrantAll(data, allPermissions)) {
		return getNewPermissionsDataForGrantAll(allPermissions, data, value);
	}

	let temp = {};
	if (value) {
		temp = {
			[permissionId]: {
				...allPermissions[permissionId],
				toggleValue: value,
			},
		};
	} else {
		data?.childDeps?.forEach((pId) => {
			temp[pId] = {
				...allPermissions[pId],
				toggleValue: value,
			};
		});
		temp[permissionId] = {
			...allPermissions[permissionId],
			toggleValue: value,
		};
	}

	const result = {
		...allPermissions,
		...temp,
	};
	return {
		newAllPermissions: result,
		newCounts: calCountPerCategory(result),
	};
};

const getNewPermissionsDataForGrantAll = (allPermissions, data, value) => {
	let temp = {};
	const { permissionId } = data;
	if (value) {
		data?.childDeps?.forEach((pId) => {
			temp[pId] = {
				...allPermissions[pId],
				toggleValue: value,
			};
		});
		temp[permissionId] = {
			...allPermissions[permissionId],
			toggleValue: value,
		};
	} else {
		const allOff =
			data.slug === "grantAll_Product" || data.slug === "view_products";
		for (let pId in allPermissions) {
			if (
				(allOff ||
					allPermissions[pId].permissionType === data.permissionType) &&
				allPermissions[pId].permissionType !== GLOBAL_PERMISSION
			) {
				temp[pId] = {
					...allPermissions[pId],
					toggleValue: value,
				};
			}
		}
	}
	const result = {
		...allPermissions,
		...temp,
	};
	return {
		newAllPermissions: result,
		newCounts: calCountPerCategory(result),
	};
};

const getSidebarAccessTags = (counts, modalType, isActive) => {
	let label;
	let color;
	if (counts.totalCount === counts.currentCount) {
		label = "Full Access";
		color = "#0B7764";
	} else if (counts.currentCount === 0 && modalType === "add") {
		label = "Select access";
		color = isActive ? "#040E0C" : "#9B9F9E";
	} else if (counts.currentCount === 0 && modalType === "edit") {
		label = "No access";
		color = isActive ? "#F34D4D" : "#9B9F9E";
	} else if (counts.currentCount < counts.totalCount) {
		label = "Partial access";
		color = "#040E0C";
	}
	return { label, color };
};

const emailValidator = (email = "") => {
	const re = /\S+@\S+\.\S+/;
	return re.test(email);
};

const validateData = (formData, modalType) => {
	if (!formData.first_name) {
		message.warning("Enter first name");
		return false;
	}

	// if (!formData.last_name) {
	// 	message.warning("Enter last name");
	// 	return false;
	// }

	if (!formData.email || !emailValidator(formData.email)) {
		message.warning("Enter a valid email");
		return false;
	}
	if (!formData.password && modalType === "add") {
		message.warning("Enter password");

		return false;
	}

	if (formData.mobile && !formData.country) {
		message.warning("Enter country code");
		return false;
	}

	if (!formData.mobile && formData.country) {
		message.warning("Enter mobile number");
		return false;
	}

	return true;
};

const getDataForNewUser = (formData, allPermissions, isAdmin = false) => {
	const { country, mobile, ...rest } = formData;
	let mobileData = {};
	if (country && mobile) {
		mobileData = {
			country,
			mobile,
		};
	}
	if (isAdmin) {
		return {
			...rest,
			...mobileData,
			isAdmin,
		};
	}
	const transformedAllPermissions = Object.values(allPermissions).map((p) => {
		return {
			permissionId: p.permissionId,
			toggleValue: p.toggleValue,
			subPermission: {
				values: p.subPermission.values || [],
			},
		};
	});
	return {
		...rest,
		...mobileData,
		isAdmin,
		permissions: transformedAllPermissions,
	};
};

const getDataForExistingUser = (formData, allPermissions, isAdmin = false) => {
	const { country, mobile, ...rest } = formData;
	let mobileData = {};
	if (country && mobile) {
		mobileData = {
			country,
			mobile,
		};
	}

	if (isAdmin) {
		return {
			first_name: formData.first_name,
			last_name: formData.last_name,
			...mobileData,
			isAdmin,
		};
	}

	const transformedAllPermissions = Object.values(allPermissions).map((p) => {
		return {
			permissionId: p.permissionId,
			toggleValue: p.toggleValue,
			subPermission: {
				values: p.subPermission.values || [],
			},
		};
	});
	return {
		first_name: formData.first_name,
		last_name: formData.last_name,
		isAdmin,
		...mobileData,
		permissions: transformedAllPermissions,
	};
};

const Spinner = () => {
	return (
		<div
			style={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<Spin />
		</div>
	);
};

const isCategoryDisabled = (p, allPermissions = {}) => {
	const a = p.slug === "global_permissions" || p.slug === "grantAll_Catalogue";
	if (!a) return false;
	if (
		p.permissionType === CATALOGUE_PERMISSION ||
		p.permissionType === GLOBAL_PERMISSION
	) {
		const item = Object.values(allPermissions).find(
			(item) => item?.slug === "view_products"
		);
		if (!item.toggleValue) return true;

		if (item?.subPermission?.values?.find((v) => v === "current_user")) {
			return true;
		}
		return false;
	}
	return false;
};

const transformUserMemberData = (data) => {
	let me;
	const newData = data.filter((user) => {
		const myself = user.userId === JSON.parse(localStorage.getItem("userId"));
		if (myself) {
			me = user;
			return false;
		}
		return true;
	});
	return [me, ...newData];
};

const updatePermissionsForWhenCurrentUser = (
	allPermissions,
	permissionId,
	newData
) => {
	let temp = {};

	for (let pId in allPermissions) {
		if (allPermissions[pId].permissionType === CATALOGUE_PERMISSION) {
			temp[pId] = {
				...allPermissions[pId],
				toggleValue: false,
			};
		}
	}

	const result = {
		...allPermissions,
		...temp,
		[permissionId]: newData,
	};
	return {
		newAllPermissions: result,
		newCounts: calCountPerCategory(result),
	};
};

const ToastError = (e) => {
	message.error(e?.response?.data?.detail);
};

const Utils = {
	tranformPermissions,
	tranformPermissionsForModal,
	Colors,
	AccessTypeBox,
	LineBreak,
	PermissionTypeBox,
	subPermissionType,
	transformToAllPermissions,
	LabelRow,
	calCountPerCategory,
	evalIfToggleIsDisabled,
	getNewPermissionsData,
	getNewPermissionsDataForGrantAll,
	getSidebarAccessTags,
	validateData,
	getDataForNewUser,
	Spinner,
	getDataForExistingUser,
	isCategoryDisabled,
	transformUserMemberData,
	updatePermissionsForWhenCurrentUser,
	ToastError,
	CATALOGUE_PERMISSION,
};

export default Utils;
